import { FC, useEffect, useMemo, useState } from 'react';
import { createEditor, Transforms } from 'slate';
import { Slate, withReact } from 'slate-react';
import { Enum } from '..';
import { useConfig } from '../../hooks/config';
import Iterator from '../../layout/Iterator';
import Colors from '../Colors';
import SignEditor from '../Editor';
import { getActive, setMark } from '../Editor/helpers';
import FontFamilies from '../FontFamilies';
import GridSelect from '../GridSelect';
import Label from '../Label';
import ListSelect from '../ListSelect';

interface iTextProps {
  value: any;
  onChange: (val: any) => void;
  formik: any;
  onLineHeightChange: (dir: number) => void;
  alignment: string;
  setAlignment: (val: string) => void;
  lineHeight: number;
}

const Text: FC<iTextProps> = ({ value, onChange, formik, onLineHeightChange, alignment, setAlignment, lineHeight }) => {
  const config = useConfig();

  const _handleChange = (key: string) => (val: string) => formik.setFieldValue(key, val);
  // Editor
  const editor = useMemo(() => withReact(createEditor() as any), []);

  const [family, setFamily] = useState(config.options?.families?.[0]?.name);
  const [color, setColor] = useState(config.options.colors[0].color);

  const _family = getActive(editor, 'family');
  const _color = getActive(editor, 'color');

  useEffect(() => {
    if (_family) setFamily(_family);
  }, [_family]);
  useEffect(() => {
    if (_color) setColor(_color);
  }, [_color]);

  const _handleMarkChange = (key: string) => (val: string) => {
    if (key === 'family' && val === family) return;
    if (key === 'color' && val === color) return;

    const _editor = { ...editor };
    const { focus, anchor } = editor.selection ?? {};

    if (!editor.selection || JSON.stringify(focus) === JSON.stringify(anchor)) {
      const lastLine = editor.children[editor.children.length - 1];
      const lastLineText = (lastLine as any).children.map((_: any) => _.text).join(' ');
      Transforms.select(editor, {
        anchor: { path: [0, 0], offset: 0 },
        focus: {
          path: [editor.children.length - 1, (lastLine as any).children.length - 1],
          offset: lastLineText.length,
        },
      });
    }

    setMark(_editor, key, val);
  };

  const _handleSizeChange = (val: string) => {
    formik.setFieldValue('size', val);
  };

  const _handleValueChange = (value: { type: string; children: { text: string }[] }[]) => {
    let isChanged = false;
    const _value = value.map((line) => ({
      ...line,
      children: line.children.map((child) => {
        const segment = child.text.split(/\s{2,}/);
        isChanged = isChanged || segment.length > 1;
        const text = segment.join(' ');
        return { ...child, text: text };
      }),
    }));
    if (isChanged) {
      const selection = {
        anchor: {
          ...editor.selection.anchor,
          offset: editor.selection.anchor.offset - 1,
        },
        focus: {
          ...editor.selection.focus,
          offset: editor.selection.focus.offset - 1,
        },
      };
      Transforms.select(editor, selection);
    }
    onChange(_value);
  };

  return (
    <Slate editor={editor} value={value} onChange={_handleValueChange as any}>
      <Iterator>
        {config.options.families && (
          <>
            <Label label='Enter text' info='Input your content here.'>
              <SignEditor
                formik={formik}
                value={value}
                onChange={onChange}
                onLineHeightChange={onLineHeightChange}
                {...{ alignment, setAlignment, lineHeight, editor }}
              />
            </Label>
          </>
        )}
        {config.options.families?.length > 0 && (
          <Label
            label='Font family'
            info='Tip: Use multiple fonts in your sign by selecting the text you want to change then choose a font.'
          >
            <FontFamilies
              // value={formik.values.family}
              value={family}
              onChange={_handleMarkChange('family')}
              families={config.options.families}
            />
          </Label>
        )}
        <Label label='Sign size' info='Choose from our popular sizes or set your own with the custom size slider.'>
          <GridSelect
            lineHeight={lineHeight}
            options={config.options.sizes}
            content={value}
            config={{
              ...formik.values,
              support: 'SHAPE',
              remote: 'NO',
              proofing: 'NO',
              mounting: 'NO',
              background: 'DEFAULT',
              power: 'WALLPLUG',
              use: 'INDOOR',
            }}
            value={formik.values.size}
            onChange={_handleSizeChange}
          />
        </Label>
        <Label
          label='Colour'
          info='Tip: Use multiple colours in your sign by selecting the text you want to change then choose a colour.'
        >
          <Colors colors={config.options.colors} value={color} onChange={_handleMarkChange('color')} />
        </Label>
        <Label
          label='Neon Tube Thickness'
          info='Available in 6mm, 8mm & 12mm tube thickness. Certain options not available in size small or medium.'
        >
          <ListSelect
            preview={value}
            config={{
              ...formik.values,
              support: 'SHAPE',
              remote: 'NO',
              proofing: 'NO',
              mounting: 'NO',
              background: 'DEFAULT',
              power: 'WALLPLUG',
              use: 'INDOOR',
            }}
            options={config.options.neons}
            value={formik.values.neon}
            onChange={_handleChange('neon')}
            priceResolver={config.priceResolver}
          />
        </Label>

        <Label label='Type of use' info='Durable and water resistant. '>
          <ListSelect
            config={{
              ...formik.values,
              support: 'SHAPE',
              remote: 'NO',
              proofing: 'NO',
              mounting: 'NO',
              background: 'DEFAULT',
              power: 'WALLPLUG',
              use: 'INDOOR',
            }}
            options={config.options.extras}
            value={formik.values.use}
            onChange={_handleChange('use')}
            preview={value}
            priceResolver={config.priceResolver}
          />
        </Label>
        <Label label='Support' info='All signs have a clear glass-like acrylic backing, choose the style to suit you.'>
          <ListSelect
            config={{
              ...formik.values,
              support: 'SHAPE',
              remote: 'NO',
              proofing: 'NO',
              mounting: 'NO',
              background: 'DEFAULT',
              power: 'WALLPLUG',
              use: 'INDOOR',
            }}
            options={config.options.supports}
            value={formik.values.support}
            onChange={_handleChange('support')}
            preview={value}
            priceResolver={config.priceResolver}
          />
        </Label>
        <Label
          label='Remote & Dimmer'
          description='Add a Remote and Dimmer (£19.95)'
          info=' Adjust the brightness to set the mood. Our signs are bright as standard, this option is recommended.'
        >
          <Enum
            isSeparated
            options={config.options.remote}
            value={formik.values.remote}
            onChange={_handleChange('remote')}
          />
        </Label>
        <Label
          label='Fast Mounting Kit'
          info='Mount your sign without drilling holes in the wall. Perfect for renters. '
          description='Mount your sign in 30 seconds no tools needed! (+£4.95) - (Screw mounting kit comes as standard with or without the Fast Mounting Kit)'
        >
          <Enum
            isSeparated
            options={config.options.mounting}
            value={formik.values.mounting}
            onChange={_handleChange('mounting')}
          />
        </Label>
        {/* <Label
          label='Power Supply'
          info='Without this service your design will be delivered to you in the shortest time.'
          description='Make your sign portable with the optional battery box (+£8.95)'
        >
          <ListSelect
            options={config.options.powerSupllies}
            value={formik.values.power}
            onChange={_handleChange('power')}
            preview={value}
            preventPreview
            config={formik.values}
            priceResolver={config.priceResolver}
          />
        </Label> */}
        <Label
          label='Proofing Service'
          info='Without this service your design will be delivered to you in the shortest time.'
          description='Receive a mock-up of your design prior to production for your approval (+£29.95)'
        >
          <Enum
            isSeparated
            options={config.options.mounting}
            value={formik.values.proofing}
            onChange={_handleChange('proofing')}
          />
        </Label>
      </Iterator>
    </Slate>
  );
};

export default Text;
